import React, { useCallback } from 'react';
import { Spinner } from '@heroui/react';
import { Typography, UniversalTable } from '../../../../components';
import { EmptyContentClientIc } from '../../../../components/icons';
import { ProposalsTableProps, ProposalData } from '../types';
import { getTableColumns } from '../constants';
import { useTranslations } from '../../../../contexts/translation.context';
import PartnerCell from './PartnerCell';
import DescriptionCell from './DescriptionCell';
import PriceCell from './PriceCell';
import ActionsCell from './ActionsCell';

const ProposalsTable: React.FC<ProposalsTableProps> = ({
  proposals,
  isLoading,
  totalCount,
  currentPage,
  pageSize,
  onPageChange,
  onPageSizeChange,
  onOrderProposal,
  openModal,
  favoriteIds,
  onToggleFavorite,
}) => {
  const { t } = useTranslations();
  const renderCell = useCallback(
    (proposal: ProposalData, columnKey: string) => {
      switch (columnKey) {
        case 'partner':
          return <PartnerCell proposal={proposal} />;
        case 'description':
          return <DescriptionCell proposal={proposal} />;
        case 'price':
          return <PriceCell proposal={proposal} />;
        case 'actions':
          return (
            <ActionsCell
              openModal={openModal}
              proposal={proposal}
              onOrderProposal={onOrderProposal}
              isFavorite={favoriteIds.includes(proposal.id)}
              onToggleFavorite={onToggleFavorite}
            />
          );
        default:
          return null;
      }
    },
    [onOrderProposal, openModal, favoriteIds, onToggleFavorite]
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Spinner size="lg" />
      </div>
    );
  }

  return (
    <UniversalTable
      data={proposals}
      columns={getTableColumns(t)}
      renderCell={renderCell}
      emptyContent={
        <div className="text-center py-12">
          <div className="flex flex-col items-center gap-4">
            <EmptyContentClientIc />
            <div className="flex flex-col gap-1.5">
              <Typography variant="h6" className="text-xl font-semibold leading-7">
                {t('proposals.noProposalsFound')}
              </Typography>
              <Typography
                className="text-neutral-500 text-base font-medium leading-6"
                variant="body1"
              >
                {t('proposals.checkDataOrTryAgain')}
              </Typography>
            </div>
          </div>
        </div>
      }
      pagination={{
        count: totalCount,
        page_size: pageSize,
        page: currentPage,
      }}
      onPageChange={onPageChange}
      onPageSizeChange={onPageSizeChange}
      currentPage={currentPage}
      currentPageSize={pageSize}
    />
  );
};

export default ProposalsTable;
